import { Button, List, Menu, MenuItem } from '@mui/material'
import React, { Fragment, useCallback, useState } from 'react'
import { Divider, Icons } from 'utoniq-ui'

import {
  CurrentUserFragment,
  Maybe,
  NotificationsConectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  NotificationBox,
  NotificationBoxSkeleton,
} from '@/components/organisms/notification'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  user: Maybe<CurrentUserFragment>
  notifications: Maybe<NotificationsConectionFragment>
}

type FilterMenuCellProps = {
  onFilter: Action['onFilter']
}

type NotificationListCellProps = Pick<
  State,
  'loading' | 'hasNextPage' | 'notifications'
> & {
  onFetchMore: Action['onFetchMore']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onFilter: (index: number) => Promise<void>
}

export const NotificationsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onFilter,
}) => {
  const { t } = useTranslation('notifications')
  const { routerNames } = useRouter()

  return (
    <BaseLayout
      navigation={{
        type: config.applicationType === 'standalone' ? 'artist' : 'global',
        props: {
          hasManagementTask: !!state.user?.hasManagementTask,
          unreadNotificationCount: state.user?.unreadNotificationCount,
          hasSettingTask: !!state.user?.isMembershipPersonalInformationRequired,
        },
      }}
      header={{ title: t('title') }}
    >
      <Head title={routerNames.Notifications} />
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-7167
       */}
      {/*<FilterMenuCell onFilter={onFilter} />*/}
      <NotificationListCell
        loading={state.loading || state.refetching}
        notifications={state.notifications}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
      />
    </BaseLayout>
  )
}

const filters = ['all', 'official']
const FilterMenuCell: Cell<FilterMenuCellProps> = ({ onFilter }) => {
  const { t } = useTranslation('notifications')
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleMenuItemClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, index: number) => {
      setSelectedIndex(index)
      setAnchorEl(null)
      onFilter(index)
    },
    [onFilter]
  )
  return (
    <CellFrame display="flex" justifyContent="flex-end" sx={{ pb: 2, px: 5 }}>
      <Button
        variant="text"
        onClick={handleClick}
        sx={{ color: 'text.primary', mr: -2 }}
      >
        {t(filters[selectedIndex])}
        <Icons.DownArrow sx={{ pl: 2 }} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {filters.map((filter, index) => (
          <MenuItem
            key={index}
            sx={{ bgcolor: 'grey.100', width: 200 }}
            selected={selectedIndex === index}
            onClick={event => handleMenuItemClick(event, index)}
          >
            {t(filter)}
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}

const NotificationListCell: Cell<NotificationListCellProps> = ({
  loading,
  hasNextPage,
  notifications,
  onFetchMore,
}) => {
  const { t } = useTranslation('notifications')
  return (
    <CellFrame>
      <ScrollFetcher
        nodes={notifications?.edges.map(({ node }, index) => (
          <Fragment key={node.id}>
            <NotificationBox notification={node} />
            {notifications?.edges.length - 1 > index && (
              <Divider sx={{ mx: 5 }} />
            )}
          </Fragment>
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 0 }}
        component={List}
        emptyMessage={t('notExist')}
        loadingComponent={
          <List>
            {[1, 2, 3, 4, 5].map(index => (
              <NotificationBoxSkeleton key={index} />
            ))}
          </List>
        }
      />
    </CellFrame>
  )
}
